import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Settings } from "lucide-react"; 

// 🔥 Socket, Toast & Firebase Imports
import { io } from "socket.io-client";
import toast, { Toaster } from "react-hot-toast";
import { messaging, requestForToken } from "./firebase"; 
import { onMessage } from "firebase/messaging";

// Contexts
import { CartProvider } from "./context/CartContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";

// Components & Pages
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import NotificationOverlay from "./components/NotificationOverlay";
import { HelmetProvider } from 'react-helmet-async';


import Home from "./pages/Home";
import Category from "./pages/Category";
import ProductDetail from "./pages/ProductDetail";
import Wishlist from "./pages/Wishlist";
import Checkout from "./pages/Checkout";
import Cart from "./pages/Cart";
import Success from "./pages/Success";
import SearchResults from "./pages/SearchResults";
import AllProducts from "./pages/AllProducts";
import Profile from "./pages/Profile";
import Referral from "./pages/Referral";
import Auth from "./pages/Auth";
import AIStylist from "./pages/AIStylist";

// Policy & Others
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Shipping from "./pages/Shipping";
import Returns from "./pages/Returns";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import CookiesPolicy from "./pages/CookiePolicy";
import AboutUs from "./pages/AboutUs";
import TrackOrder from './pages/TrackOrder';

// Admin
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AddProduct from "./pages/admin/AddProduct";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminRequests from "./pages/admin/AdminRequests";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminNotifications from "./pages/admin/AdminNotifications";
import BulkLeads from "./pages/admin/BulkLeads";

const socket = io("https://wethrift-backend.onrender.com");
window.socket = socket;

const MaintenancePage = () => (
  <div className="min-h-screen bg-black flex flex-col items-center justify-center p-10 text-center font-sans">
    <div className="w-24 h-24 bg-emerald-500/10 rounded-full flex items-center justify-center animate-pulse mb-8 border border-emerald-500/20">
      <Settings className="text-emerald-500" size={48} />
    </div>
    <h1 className="text-6xl font-black italic text-white mb-4 uppercase tracking-tighter">
      ARCHIVE <span className="text-zinc-700 underline decoration-emerald-500">OFFLINE</span>
    </h1>
    <p className="text-zinc-500 max-w-md font-bold uppercase text-xs tracking-widest leading-relaxed">
      System upgrading... Back online shortly.
    </p>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/auth" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user?.isAdmin ? children : <Navigate to="/" replace />;
};

const LayoutWrapper = () => {
  const { user, loading: authLoading } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [fetchingStatus, setFetchingStatus] = useState(true);
  const [incomingNotify, setIncomingNotify] = useState(null); 

  const isMobileApp = window.navigator.userAgent.includes('WethriftApp');

  // 🔥 1. PUSH NOTIFICATION SYNC LOGIC
  useEffect(() => {
    const setupNotifications = async () => {
      try {
        if (!messaging) return;
        
        // Request Permission
        const permission = await Notification.requestPermission();
        
        if (permission === "granted" && user) {
          const fcmToken = await requestForToken();

          if (fcmToken) {
            // LocalStorage se data uthana (Tumhare structure ke hisaab se)
            const rawData = localStorage.getItem("weThriftUser");
            if (!rawData) return;
            
            const parsedData = JSON.parse(rawData);
            // Bearer Token check (accessToken ya token jo bhi tum use kar rahe ho)
            const authToken = parsedData.token || parsedData.accessToken;

            if (authToken) {
              const cleanAuthToken = authToken.replace(/"/g, '');
              
              // 🚀 API CALL: Token sync with Backend
              await axios.post(`https://wethrift-backend.onrender.com/api/users/save-token`, 
                { pushToken: fcmToken }, 
                { 
                  headers: { 
                    token: `Bearer ${cleanAuthToken}` 
                  } 
                }
              );
              console.log("🚀 Notification System Live & Synced!");
            }
          }
        }
      } catch (err) {
        // Backend 403 Forbidden yahan handle hoga
        console.error("❌ Sync Error:", err.response?.data || err.message);
      }
    };

    if (user) {
      setupNotifications();
    }

    // Foreground Message Listener
    if (messaging) {
      const unsubscribe = onMessage(messaging, (payload) => {
        setIncomingNotify({
          title: payload.notification.title,
          message: payload.notification.body,
          type: "new_drop",
        });
        
        // Notification Sound
        const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3");
        audio.play().catch((e) => console.log("Audio Play Blocked", e));
        
        setTimeout(() => setIncomingNotify(null), 6500);
      });
      return () => unsubscribe();
    }
  }, [user]);

  // 🔥 2. SOCKET.IO LOGIC
  useEffect(() => {
    if (socket) {
      if (user?._id) {
        socket.emit("join_user_room", user._id);
      }

      socket.on("order_notification", (data) => {
        setIncomingNotify(data);
        const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3"); 
        audio.play().catch(() => {});
        setTimeout(() => setIncomingNotify(null), 6500);
      });

      socket.on("show_order_toast", (data) => {
        toast.custom((t) => (
          <div className={`${t.visible ? 'animate-in fade-in slide-in-from-bottom' : 'animate-out fade-out'} max-w-md w-full bg-zinc-900/95 border-l-4 border-l-emerald-500 p-4 rounded-2xl shadow-2xl backdrop-blur-md`}>
            <p className="text-[10px] font-black uppercase text-emerald-500 tracking-widest italic">Live_Transmission</p>
            <p className="text-sm font-bold text-zinc-300 italic">User in {data.location} secured "{data.item}"</p>
          </div>
        ), { position: 'bottom-left' });
      });
    }

    return () => {
      if(socket) {
        socket.off("order_notification");
        socket.off("show_order_toast");
      }
    };
  }, [user]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get("https://wethrift-backend.onrender.com/api/settings");
        setIsMaintenance(res.data ? res.data.status : false);
      } catch (err) { 
        setIsMaintenance(false); 
      } finally { 
        setFetchingStatus(false); 
      }
    };
    checkStatus();
  }, []);

  if (authLoading || fetchingStatus) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isAdminPath = location.pathname.startsWith("/admin");
  if (isMaintenance && !user?.isAdmin) return <MaintenancePage />;

  return (
    <div className={`flex flex-col min-h-screen transition-colors duration-500 ${isDarkMode ? "bg-black text-white" : "bg-white text-black"}`}>
      <NotificationOverlay data={incomingNotify} close={() => setIncomingNotify(null)} />
      <Toaster position="top-right" reverseOrder={false} />

      {!isAdminPath && !isMobileApp && (
        <>
          <Navbar /> 
          <CartDrawer />
        </>
      )}

      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/ai-stylist" element={<AIStylist />} />
          <Route path="/products" element={<AllProducts />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/category/:type" element={<Category />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
          <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/refer" element={<ProtectedRoute><Referral /></ProtectedRoute>} />
          <Route path="/success" element={<ProtectedRoute><Success /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="add-product" element={<AddProduct />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="requests" element={<AdminRequests />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="bulk-leads" element={<BulkLeads />} />
          </Route>
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookie-policy" element={<CookiesPolicy />} />
          <Route path="/track/:id" element={<TrackOrder />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {!isAdminPath && !isMobileApp && <Footer />}
    </div>
  );
};

export default function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <ThemeProvider>
        <CartProvider>
          
          <Router>
            <LayoutWrapper />
          </Router>
        </CartProvider>
      </ThemeProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}